import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { reasonOptions, findReasonOption } from '../utils/disposeUtils'
import InventoryItemCard from '../components/InventoryItemCard'
import QuantityPicker from '../components/QuantityPicker'
import { usePermissions } from '../contexts/PermissionContext'
import Select from 'react-select'
import '../styles/selectControl.css'

function InventoryDisposeScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { inventoryItemId } = useParams()
    const [searchParams] = useSearchParams()
    const quantityRef = useRef(null)
    const [error, setError] = useState(null)
    const [inventoryItem, setInventoryItem] = useState({
        stock_item: null,
        warehouse: { _id: '' },
        location: '',
        reason: '',
        quantity: '',
        notes: '',
        max_quantity: 0,

    })

    useEffect(() => {
        async function fetchInventoryItem() {
            try {
                const item = await inventoryItemAPI.getById(inventoryItemId)
                if (!item) return;

                const formattedItem = {
                    stock_item: item.stock_item,
                    warehouse_name: item.warehouse_name,
                    location: item.location_name,
                    quantity: '',
                    reason: item.reason || '',
                    notes: item.notes || '',
                    max_quantity: item.quantity || 0,
                    vendor: item.vendor_name,
                    manufacturer: item.manufacturer_name,
                    cost: item.cost,
                    lot: item.lot,
                    bb_date: item.bb_date                
                }
                
                setInventoryItem(formattedItem)
            } catch (error) {
                setError('Failed to load inventory item')
            }
        }

        if (inventoryItemId) {
            fetchInventoryItem()
        }
    }, [inventoryItemId])

    const submitHandler = async (e) => {
        e.preventDefault()
        if (!inventoryItem.quantity || inventoryItem.quantity === '') {
            setError('Please enter a quantity')
            quantityRef.current?.focus()
            return
        }
        try {
            await inventoryItemAPI.dispose(inventoryItemId, inventoryItem.quantity, inventoryItem.reason, inventoryItem.notes)
            
            // Check for returnTo query parameter first, then fall back to location state filters
            const returnTo = searchParams.get('returnTo')
            if (returnTo) {
                navigate(returnTo)
            } else {
                // Get the return filters from the location state if they exist
                const returnFilters = location.state?.returnFilters || ''
                
                // Add refresh parameter to trigger data refresh
                const separator = returnFilters.includes('?') ? '&' : '?'
                const refreshParam = `${separator}refresh=1`
                
                // Navigate back to inventory with the filters preserved and refresh trigger
                navigate(`/inventory${returnFilters}${refreshParam}`)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('inventory.dispose')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to dispose inventory items.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
            <Col style={{ maxWidth: '730px' }}>
            <h1>Dispose Inventory Item {inventoryItemId}</h1>
            {inventoryItem?.stock_item && (
                <InventoryItemCard inventoryItem={inventoryItem} />
            )}
            <div style={{ height: '20px' }}></div>
            <Form onSubmit={submitHandler}>
                <Form.Group className="mb-4" controlId="quantity">
                    <Form.Label>Quantity*</Form.Label>
                    <QuantityPicker
                        ref={quantityRef}
                        casePack={inventoryItem.stock_item?.case_pack || 1}
                        uom={inventoryItem.stock_item?.uom || ''}
                        value={inventoryItem.quantity}
                        onChange={(qty) => setInventoryItem({...inventoryItem, quantity: qty})}
                        required
                        max={inventoryItem.max_quantity}
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="reason">
                    <Form.Label>Reason*</Form.Label>
                    <Select
                        value={findReasonOption(inventoryItem.reason)}
                        onChange={(selectedOption) => setInventoryItem({
                            ...inventoryItem, 
                            reason: selectedOption ? selectedOption.value : ''
                        })}
                        options={reasonOptions}
                        placeholder="Select Reason"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="notes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        value={inventoryItem.notes}
                        onChange={(e) => setInventoryItem({...inventoryItem, notes: e.target.value})}
                        placeholder="Enter notes (max 500 characters)"
                        autoComplete="off"
                        spellCheck="true"
                    />
                    <Form.Text className="text-muted">
                        {inventoryItem.notes?.length || 0}/500 characters
                    </Form.Text>
                </Form.Group>
                {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
                )}
                <Button variant="danger" type="submit" className="me-2">
                    Dispose
                </Button>

                <Button 
                    variant="secondary" 
                    onClick={() => navigate(-1)}
                >
                    Cancel
                </Button>
            </Form>
            
            </Col>
            </Row>
        </Container>
    )
}

export default InventoryDisposeScreen 